import { useStore, create } from 'zustand';
import { persist } from 'zustand/middleware';
import { pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { GeneralUser } from '@mews-commander/data-access';
import { isDateTimeUtcOlderThan23Hours } from '@mews-commander/utils';
import { useAppNowUtc } from '@mews/framework';

interface LockedUser {
    user: GeneralUser;
    accessToken: string;
    nowUtc: string;
    deviceId: string;
}

interface LockscreenStore {
    users: {
        [id: string]: LockedUser;
    };
    selectedUserId: null | string;
    lockUserSession: (lockedUser: LockedUser) => void;
    deleteLockedUser: (userId: string) => void;
    selectLockedUser: (userId: string | null) => void;
}

const lockscreenStore = create<LockscreenStore>()(
    persist(
        (set) => ({
            users: {},
            selectedUserId: null,
            lockUserSession: ({ user, accessToken, nowUtc, deviceId }) =>
                set((state) => ({
                    users: { ...state.users, [user.id]: { user, accessToken, nowUtc, deviceId } },
                    selectedUserId: user.id,
                })),
            deleteLockedUser: (userId) =>
                set((state) => {
                    const { [userId]: deletedUser, ...others } = state.users;
                    return {
                        users: others,
                        selectedUserId: state.selectedUserId === userId ? null : state.selectedUserId,
                    };
                }),
            selectLockedUser: (userId) => set(() => ({ selectedUserId: userId })),
        }),
        {
            name: 'lockscreen',
        }
    )
);

const useLockscreenStore = () => useStore(lockscreenStore);

export const { lockUserSession, deleteLockedUser, selectLockedUser } = lockscreenStore.getState();

export const useLockedUsers = (): Record<string, LockedUser> => {
    const appNowUtc = useAppNowUtc();
    const { users } = useLockscreenStore();

    return pipe(
        users,
        record.filter(
            (lockedUser) =>
                lockedUser.nowUtc !== undefined && !isDateTimeUtcOlderThan23Hours(appNowUtc, lockedUser.nowUtc)
        )
    );
};

export const useSelectedLockedUser = () => {
    const users = useLockedUsers();
    const { selectedUserId } = useLockscreenStore();

    if (!selectedUserId || !users[selectedUserId]) {
        return null;
    }

    return users[selectedUserId];
};

export const useHasLockedUsers = () => Object.values(useLockedUsers()).length > 0;
